import { useMemo, useState } from 'react';
import { FunnelChart, Funnel, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Filter, TrendingDown, Info, ShoppingCart, Eye, MousePointerClick, Users, Search, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { cn, formatCompact } from '@/lib/utils';
import { useGSCKPIs } from '@/hooks/useGSCData';
import { useGA4KPIs } from '@/hooks/useGA4Data';
import { useGA4EcommerceFunnel, useGA4AvailableEvents } from '@/hooks/useGA4EcommerceData';
import { useAuthStore } from '@/stores/auth-store';

type FunnelType = 'journey' | 'ecommerce';

/**
 * Conversion Funnel View - Visual funnel chart showing conversion steps
 * Two modes:
 * 1. Journey Funnel: Impressions → Clicks → Sessions → Users (GSC + GA4)
 * 2. E-commerce Funnel: Sessions → Product Views → Add to Cart → Purchase (GA4 e-commerce)
 */
export function ConversionFunnelView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;
  const hasGA4 = !!activeProperty?.ga4Property;

  const [funnelType, setFunnelType] = useState<FunnelType>('journey');
  const [showAvailableEvents, setShowAvailableEvents] = useState(false);

  const { data: gscKpis, isLoading: gscLoading } = useGSCKPIs();
  const { data: ga4Kpis, isLoading: ga4Loading } = useGA4KPIs();
  const { data: ecommerceData, isLoading: ecommerceLoading } = useGA4EcommerceFunnel();
  const { data: availableEvents, isLoading: eventsLoading } = useGA4AvailableEvents();

  const isLoading = gscLoading || ga4Loading || ecommerceLoading;

  // Categorize available events
  const eventCategories = useMemo(() => {
    if (!availableEvents) return null;

    const ecommerceEvents = ['view_item', 'add_to_cart', 'remove_from_cart', 'begin_checkout', 'add_payment_info', 'add_shipping_info', 'purchase', 'refund', 'view_cart', 'view_item_list', 'select_item', 'select_promotion', 'view_promotion'];
    const engagementEvents = ['page_view', 'scroll', 'click', 'file_download', 'video_start', 'video_progress', 'video_complete', 'form_start', 'form_submit'];
    const conversionEvents = ['sign_up', 'login', 'generate_lead', 'tutorial_begin', 'tutorial_complete', 'level_start', 'level_end', 'level_up', 'unlock_achievement', 'post_score', 'spend_virtual_currency', 'earn_virtual_currency'];

    const categorized = {
      ecommerce: [] as typeof availableEvents,
      engagement: [] as typeof availableEvents,
      conversion: [] as typeof availableEvents,
      custom: [] as typeof availableEvents,
    };

    for (const event of availableEvents) {
      if (ecommerceEvents.includes(event.eventName)) {
        categorized.ecommerce.push(event);
      } else if (engagementEvents.includes(event.eventName)) {
        categorized.engagement.push(event);
      } else if (conversionEvents.includes(event.eventName)) {
        categorized.conversion.push(event);
      } else {
        categorized.custom.push(event);
      }
    }

    return categorized;
  }, [availableEvents]);

  // Check for key e-commerce events
  const hasEcommerceTracking = useMemo(() => {
    if (!availableEvents) return { viewItem: false, addToCart: false, checkout: false, purchase: false };
    const eventNames = availableEvents.map((e: { eventName: string }) => e.eventName);
    return {
      viewItem: eventNames.includes('view_item'),
      addToCart: eventNames.includes('add_to_cart'),
      checkout: eventNames.includes('begin_checkout'),
      purchase: eventNames.includes('purchase'),
    };
  }, [availableEvents]);

  // Journey Funnel Data (GSC → GA4)
  const journeyFunnelData = useMemo(() => {
    if (!gscKpis && !ga4Kpis) return null;

    const data = [];
    let previousValue = 0;

    if (gscKpis?.impressions) {
      data.push({
        name: 'Impressions',
        value: gscKpis.impressions,
        fill: '#10b981', // emerald
        icon: Eye,
        description: 'Times shown in search',
      });
      previousValue = gscKpis.impressions;
    }

    if (gscKpis?.clicks) {
      const convRate = previousValue > 0 ? (gscKpis.clicks / previousValue) * 100 : 0;
      data.push({
        name: 'Clicks',
        value: gscKpis.clicks,
        fill: '#34d399', // emerald-400
        icon: MousePointerClick,
        description: `${convRate.toFixed(1)}% CTR`,
        conversionRate: convRate,
      });
      previousValue = gscKpis.clicks;
    }

    if (ga4Kpis?.sessions) {
      const convRate = previousValue > 0 ? (ga4Kpis.sessions / previousValue) * 100 : 0;
      data.push({
        name: 'Sessions',
        value: ga4Kpis.sessions,
        fill: '#3b82f6', // blue
        icon: Search,
        description: `${convRate.toFixed(0)}% of clicks`,
        conversionRate: convRate,
      });
      previousValue = ga4Kpis.sessions;
    }

    if (ga4Kpis?.users) {
      const convRate = previousValue > 0 ? (ga4Kpis.users / previousValue) * 100 : 0;
      data.push({
        name: 'Users',
        value: ga4Kpis.users,
        fill: '#8b5cf6', // purple
        icon: Users,
        description: `${convRate.toFixed(0)}% unique`,
        conversionRate: convRate,
      });
    }

    return data;
  }, [gscKpis, ga4Kpis]);

  // E-commerce Funnel Data
  const ecommerceFunnelData = useMemo(() => {
    if (!ecommerceData) return null;

    const data = [];
    let previousValue = 0;

    if (ecommerceData.sessions) {
      data.push({
        name: 'Sessions',
        value: ecommerceData.sessions,
        fill: '#3b82f6',
        icon: Search,
        description: 'All sessions started',
      });
      previousValue = ecommerceData.sessions;
    }

    if (ecommerceData.productViews !== undefined) {
      const convRate = previousValue > 0 ? (ecommerceData.productViews / previousValue) * 100 : 0;
      data.push({
        name: 'Product Views',
        value: ecommerceData.productViews,
        fill: '#8b5cf6',
        icon: Eye,
        description: `${convRate.toFixed(1)}% viewed products`,
        conversionRate: convRate,
      });
      previousValue = ecommerceData.productViews;
    }

    if (ecommerceData.addToCarts !== undefined) {
      const convRate = previousValue > 0 ? (ecommerceData.addToCarts / previousValue) * 100 : 0;
      data.push({
        name: 'Add to Cart',
        value: ecommerceData.addToCarts,
        fill: '#ec4899',
        icon: ShoppingCart,
        description: `${convRate.toFixed(1)}% added to cart`,
        conversionRate: convRate,
      });
      previousValue = ecommerceData.addToCarts;
    }

    if (ecommerceData.purchases !== undefined) {
      const convRate = previousValue > 0 ? (ecommerceData.purchases / previousValue) * 100 : 0;
      data.push({
        name: 'Purchase',
        value: ecommerceData.purchases,
        fill: '#f43f5e',
        icon: ShoppingCart,
        description: `${convRate.toFixed(1)}% purchased`,
        conversionRate: convRate,
      });
    }

    return data.length >= 2 ? data : null;
  }, [ecommerceData]);

  const activeFunnelData = funnelType === 'journey' ? journeyFunnelData : ecommerceFunnelData;

  // Calculate overall conversion rate
  const overallConversion = useMemo(() => {
    if (!activeFunnelData || activeFunnelData.length < 2) return null;
    const first = activeFunnelData[0].value;
    const last = activeFunnelData[activeFunnelData.length - 1].value;
    return first > 0 ? (last / first) * 100 : 0;
  }, [activeFunnelData]);

  const hasEcommerceData = ecommerceFunnelData && ecommerceFunnelData.length >= 2;

  if (!hasGSC && !hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No property connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Filter className="h-6 w-6 text-primary" />
            Conversion Funnel
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visualize drop-off at each step of the user journey
          </p>
        </div>

        {/* Funnel Type Toggle */}
        <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
          <button
            onClick={() => setFunnelType('journey')}
            className={cn(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              funnelType === 'journey'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Search Journey
          </button>
          <button
            onClick={() => setFunnelType('ecommerce')}
            disabled={!hasEcommerceData}
            className={cn(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
              funnelType === 'ecommerce'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
              !hasEcommerceData && 'opacity-50 cursor-not-allowed'
            )}
            title={!hasEcommerceData ? 'No e-commerce data available' : 'E-commerce conversion funnel'}
          >
            E-commerce
          </button>
        </div>
      </div>

      {/* Overall Conversion Rate */}
      {overallConversion !== null && (
        <div className="glass-panel p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-success/20 flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overall Conversion Rate</p>
              <p className="text-2xl font-bold text-success">{overallConversion.toFixed(1)}%</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {activeFunnelData?.[0]?.name} → {activeFunnelData?.[activeFunnelData.length - 1]?.name}
          </p>
        </div>
      )}

      {/* Funnel Chart */}
      <div className="glass-panel p-6 flex-1 min-h-[400px]">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading funnel data...
          </div>
        ) : !activeFunnelData || activeFunnelData.length < 2 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Filter className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {funnelType === 'ecommerce'
                  ? 'No e-commerce tracking data available. Enable e-commerce events in GA4.'
                  : 'Insufficient data for funnel visualization'}
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex">
            {/* Funnel Visualization */}
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <FunnelChart>
                  <Tooltip content={<CustomTooltip />} />
                  <Funnel
                    dataKey="value"
                    data={activeFunnelData}
                    isAnimationActive
                  >
                    {activeFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                    <LabelList
                      position="right"
                      content={<CustomLabel data={activeFunnelData} />}
                    />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </div>

            {/* Step Details Sidebar */}
            <div className="w-64 pl-6 space-y-3">
              {activeFunnelData.map((step, index) => (
                <FunnelStepCard
                  key={step.name}
                  step={step}
                  index={index}
                  isLast={index === activeFunnelData.length - 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info Panel */}
      {funnelType === 'ecommerce' && !hasEcommerceData && (
        <div className="glass-panel p-4">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-primary mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">E-commerce Tracking Required</p>
              <p className="text-xs">
                To see e-commerce funnel data, you need to implement GA4 e-commerce events:
                <code className="mx-1 px-1 bg-muted rounded">view_item</code>,
                <code className="mx-1 px-1 bg-muted rounded">add_to_cart</code>,
                <code className="mx-1 px-1 bg-muted rounded">purchase</code>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Available Events Panel */}
      {hasGA4 && (
        <div className="glass-panel">
          <button
            onClick={() => setShowAvailableEvents(!showAvailableEvents)}
            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" />
              <span className="font-medium text-foreground">Available GA4 Events</span>
              {availableEvents && (
                <span className="text-xs text-muted-foreground">
                  ({availableEvents.length} events tracked)
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* E-commerce tracking status badges */}
              <div className="flex items-center gap-1">
                <EventStatusBadge label="view_item" active={hasEcommerceTracking.viewItem} />
                <EventStatusBadge label="add_to_cart" active={hasEcommerceTracking.addToCart} />
                <EventStatusBadge label="checkout" active={hasEcommerceTracking.checkout} />
                <EventStatusBadge label="purchase" active={hasEcommerceTracking.purchase} />
              </div>
              <ChevronDown className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                showAvailableEvents && "rotate-180"
              )} />
            </div>
          </button>

          {showAvailableEvents && (
            <div className="border-t border-border p-4">
              {eventsLoading ? (
                <div className="text-sm text-muted-foreground animate-pulse">
                  Loading available events...
                </div>
              ) : !eventCategories ? (
                <div className="text-sm text-muted-foreground">
                  No event data available. Make sure GA4 is properly connected.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {/* E-commerce Events */}
                  <EventCategoryCard
                    title="E-commerce Events"
                    icon={ShoppingCart}
                    events={eventCategories.ecommerce}
                    color="#ec4899"
                    emptyMessage="No e-commerce tracking implemented"
                  />

                  {/* Conversion Events */}
                  <EventCategoryCard
                    title="Conversion Events"
                    icon={CheckCircle}
                    events={eventCategories.conversion}
                    color="#10b981"
                    emptyMessage="No conversion events tracked"
                  />

                  {/* Engagement Events */}
                  <EventCategoryCard
                    title="Engagement Events"
                    icon={MousePointerClick}
                    events={eventCategories.engagement}
                    color="#3b82f6"
                    emptyMessage="Standard engagement events"
                  />

                  {/* Custom Events */}
                  <EventCategoryCard
                    title="Custom Events"
                    icon={AlertCircle}
                    events={eventCategories.custom}
                    color="#8b5cf6"
                    emptyMessage="No custom events"
                  />
                </div>
              )}

              {/* Data Availability Summary */}
              <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">About Conversion Funnels</p>
                    <p>
                      <strong>Search Journey:</strong> Uses GSC impressions/clicks + GA4 sessions/users.
                      This shows the search-to-site flow but not on-site conversions.
                    </p>
                    <p className="mt-1">
                      <strong>E-commerce Funnel:</strong> Requires your website to implement GA4 e-commerce events
                      (view_item, add_to_cart, begin_checkout, purchase). Without these, the funnel will be empty.
                    </p>
                    <p className="mt-1">
                      <strong>Custom Funnels:</strong> Any events shown above can potentially be used to build
                      custom conversion funnels based on your site's specific user journey.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Event status badge component
function EventStatusBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span
      className={cn(
        "px-1.5 py-0.5 rounded text-[10px] font-medium",
        active
          ? "bg-success/20 text-success"
          : "bg-muted text-muted-foreground"
      )}
      title={active ? `${label} is being tracked` : `${label} is not tracked`}
    >
      {active ? <CheckCircle className="h-3 w-3 inline mr-0.5" /> : <AlertCircle className="h-3 w-3 inline mr-0.5" />}
      {label}
    </span>
  );
}

// Event category card component
interface EventCategoryCardProps {
  title: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  events: Array<{ eventName: string; eventCount: number }>;
  color: string;
  emptyMessage: string;
}

function EventCategoryCard({ title, icon: Icon, events, color, emptyMessage }: EventCategoryCardProps) {
  return (
    <div className="p-3 rounded-lg border border-border bg-muted/20">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">({events.length})</span>
      </div>
      {events.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{emptyMessage}</p>
      ) : (
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {events.map((event) => (
            <div key={event.eventName} className="flex items-center justify-between text-xs">
              <code className="text-foreground">{event.eventName}</code>
              <span className="text-muted-foreground">{formatCompact(event.eventCount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface FunnelStepCardProps {
  step: {
    name: string;
    value: number;
    fill: string;
    description: string;
    conversionRate?: number;
    icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  };
  index: number;
  isLast: boolean;
}

function FunnelStepCard({ step, index: _index, isLast }: FunnelStepCardProps) {
  const Icon = step.icon;

  return (
    <div
      className="p-3 rounded-lg border-l-4 bg-muted/30"
      style={{ borderLeftColor: step.fill }}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color: step.fill }} />
        <span className="text-sm font-medium text-foreground">{step.name}</span>
      </div>
      <p className="text-xl font-bold text-foreground mt-1">
        {formatCompact(step.value)}
      </p>
      <p className="text-xs text-muted-foreground">{step.description}</p>
      {!isLast && step.conversionRate !== undefined && (
        <div className="mt-2 flex items-center gap-1 text-xs">
          <TrendingDown className="h-3 w-3 text-warning" />
          <span className="text-warning">
            {(100 - step.conversionRate).toFixed(1)}% drop-off
          </span>
        </div>
      )}
    </div>
  );
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload[0]) return null;

  const data = payload[0].payload;

  return (
    <div className="glass-panel p-3 shadow-lg border border-border">
      <p className="font-medium text-foreground">{data.name}</p>
      <p className="text-2xl font-bold mt-1" style={{ color: data.fill }}>
        {formatCompact(data.value)}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{data.description}</p>
      {data.conversionRate !== undefined && (
        <p className="text-xs text-muted-foreground mt-1">
          Conversion: {data.conversionRate.toFixed(1)}%
        </p>
      )}
    </div>
  );
}

function CustomLabel({ data, ...props }: any) {
  const { x, y, width, height, index } = props;
  const entry = data[index];

  if (!entry) return null;

  return (
    <g>
      <text
        x={x + width + 10}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="middle"
        className="fill-foreground text-sm font-medium"
      >
        {entry.name}
      </text>
      <text
        x={x + width + 10}
        y={y + height / 2 + 16}
        textAnchor="start"
        dominantBaseline="middle"
        className="fill-muted-foreground text-xs"
      >
        {formatCompact(entry.value)}
      </text>
    </g>
  );
}
